// Plan selection dialog/drawer component for viewing all plans
import { ResponsiveDrawer } from "@/src/components/ui/ResponsiveDrawer";
import { CountryStripePriceInfo } from "@/src/features/pricing/types";
import { useState } from "react";
import { PlanButton } from "./PlanButton";
import { StartFreeTrialButton } from "./StartFreeTrialButton";
import { TrialDetails } from "./TrialDetails";

interface PlanSelectionDialogProps {
  trigger?: React.ReactNode;
  prices?: CountryStripePriceInfo[];
  hasHadATrial: boolean;
  couponCode?: string;
  currentUser: any;
  currentStepIndex: number;
  logOnboardingStep: (
    currentStepIndex: number,
    currentStepKey: string,
    currentUser: any,
    otherObjective: string,
    otherText: string,
    otherCategory: string
  ) => void;
  otherObjective: string;
  otherText: string;
  otherCategory: string;
  onPlanSelected?: (
    price: CountryStripePriceInfo,
    planType: "yearly" | "monthly"
  ) => void;
}

export const PlanSelectionDialog = ({
  trigger,
  prices = [],
  hasHadATrial,
  couponCode,
  currentUser,
  currentStepIndex,
  logOnboardingStep,
  otherObjective,
  otherText,
  otherCategory,
  onPlanSelected,
}: PlanSelectionDialogProps) => {
  const [selectedPlan, setSelectedPlan] = useState<{
    price: CountryStripePriceInfo;
    planType: "yearly" | "monthly";
  } | null>(null);

  // Find yearly and monthly prices
  const yearlyPrice = prices.find(
    (price) => price.recurring?.interval === "year"
  );
  const monthlyPrice = prices.find(
    (price) => price.recurring?.interval === "month"
  );

  const handlePlanSelect = (
    price: CountryStripePriceInfo,
    planType: "yearly" | "monthly"
  ) => {
    setSelectedPlan({ price, planType });
    onPlanSelected?.(price, planType);
  };

  const calculateSavings = () => {
    if (!yearlyPrice || !monthlyPrice) return null;

    const yearlyMonthlyEquivalent = Math.round(
      (yearlyPrice.unit_amount ?? 0) / 12
    );
    const monthlyAmount = monthlyPrice.unit_amount ?? 0;
    const savings = monthlyAmount - yearlyMonthlyEquivalent;
    const savingsPercentage = Math.round((savings / monthlyAmount) * 100);

    return savingsPercentage;
  };

  const savingsPercentage = calculateSavings();

  return (
    <ResponsiveDrawer
      title="Select your plan"
      description="Choose your subscription plan"
      trigger={trigger}
      dialogClassName="max-w-md bg-white"
      drawerClassName=""
    >
      <div>
        <div className="space-y-2">
          {/* Yearly Plan */}
          {yearlyPrice && (
            <PlanButton
              price={yearlyPrice}
              planType="yearly"
              isSelected={selectedPlan?.planType === "yearly"}
              onSelect={() => handlePlanSelect(yearlyPrice, "yearly")}
              savingsBadge={
                savingsPercentage && savingsPercentage > 0
                  ? `SAVE ${savingsPercentage}%`
                  : undefined
              }
            />
          )}

          {/* Monthly Plan */}
          {monthlyPrice && (
            <PlanButton
              price={monthlyPrice}
              planType="monthly"
              isSelected={selectedPlan?.planType === "monthly"}
              onSelect={() => handlePlanSelect(monthlyPrice, "monthly")}
            />
          )}
        </div>

        <div className="flex flex-col space-y-3">
          {/* Start Free Trial Button */}
          <StartFreeTrialButton
            selectedPlan={selectedPlan}
            defaultPrice={yearlyPrice || monthlyPrice}
            hasHadATrial={hasHadATrial}
            couponCode={couponCode}
            currentUser={currentUser}
            currentStepIndex={currentStepIndex}
            logOnboardingStep={logOnboardingStep}
            otherObjective={otherObjective}
            otherText={otherText}
            otherCategory={otherCategory}
            onComplete={undefined}
            className="mt-4"
          />

          {/* Trial Details */}
          <TrialDetails
            selectedPlan={selectedPlan}
            defaultPrice={yearlyPrice || monthlyPrice}
          />
        </div>
      </div>
    </ResponsiveDrawer>
  );
};
